import type { Config, CoreProjection } from '@/adapters/core/_contract/config';
import type { VisualNode } from '@/adapters/core/_pure/projection';
import { deriveRootRingLayout } from './coreLayout';
import type { Vector3Tuple } from '@toolproof-core/visualization/_contract/geometry';
import { collectPackageTree } from './packageTree';
import { Euler, Quaternion, Vector3 } from 'three';
import { compactStandingLayout } from './compactStandingLayout';

export function deriveRingTree(projection: CoreProjection, config: Config) {
    const settings = config.ringTree ?? { radius: 48, radialLength: 22, folderRingRadius: 10 };
    const orientation = settings.orientation ?? 'horizontal';
    if (orientation !== 'horizontal' && orientation !== 'standing') {
        throw new Error(`Unknown Core ring-tree orientation: ${orientation}`);
    }
    if (![settings.radius, settings.radialLength, settings.folderRingRadius].every(value => Number.isFinite(value) && value > 0)) {
        throw new Error('Ring-tree radii and radial length must be finite positive numbers.');
    }
    const tree = collectPackageTree(projection);
    const treeByPath = new Map(tree.map(node => [node.path, node]));
    const nodes = tree.filter(node => node.depth <= 1).map(node => ({
        ...node, childPaths: node.depth === 0 ? [...node.childPaths] : [],
    }));
    const packageNodes = nodes.filter(node => node.depth === 1);
    const layout = deriveRootRingLayout({
        root: nodes[0]!, children: packageNodes, radius: settings.radius,
        rootRadius: config.geometry.rootRadius * config.geometry.renderRadiusMultiplier,
        childRadius: config.geometry.directoryRadius * config.geometry.renderRadiusMultiplier,
        gap: config.geometry.minimumSubtreeGap,
    });
    let initialBoundsRadius = layout.boundsRadius;
    const rotationByPath = new Map<string, Vector3Tuple>();
    const radiusByPath = new Map<string, number>();
    const childRings: {
        parentPath: string; center: Vector3Tuple; rotation: Vector3Tuple; nodes: VisualNode[];
        sphereRadius: number; coneHalfAngle: number; clearanceRadius: number;
    }[] = [];
    const maxSphereRadius = config.geometry.directoryRadius * config.geometry.renderRadiusMultiplier;
    const maxFileRadius = config.geometry.fileRadius * config.geometry.renderRadiusMultiplier;
    const folderRadius = settings.folderRingRadius;
    const count = packageNodes.length;
    const gap = config.geometry.minimumSubtreeGap;
    if (![maxSphereRadius, maxFileRadius, gap].every(value => Number.isFinite(value) && value > 0)) {
        throw new Error('Ring-tree node radii and clearance must be finite positive numbers.');
    }
    const standing = orientation === 'standing';
    const parallelRotation: Vector3Tuple = [Math.PI / 2, 0, 0];
    const packageCone = count > 1 ? Math.PI / count * 0.9 : Math.PI / 3;
    if (standing && count > 0) {
        const rootRadius = layout.entryByPath.get('.')!.visualRadius;
        const depth = Math.max(settings.radialLength, rootRadius + maxSphereRadius + gap);
        const center: Vector3Tuple = [0, 0, -depth];
        layout.ringCenterByPath.set('.', center);
        rotationByPath.set('.', parallelRotation);
        for (const node of packageNodes) {
            const entry = layout.entryByPath.get(node.path)!;
            entry.position = [entry.position[0], -entry.position[2], -depth];
        }
        const clearanceRadius = layout.ringRadiusByPath.get('.')! + maxSphereRadius + gap;
        childRings.push({ parentPath: '.', center, rotation: parallelRotation, nodes: packageNodes,
            sphereRadius: maxSphereRadius, coneHalfAngle: Math.PI / 2, clearanceRadius });
        layout.boundsRadius = Math.max(layout.boundsRadius, depth + clearanceRadius);
        initialBoundsRadius = layout.boundsRadius;
    }
    type PendingRing = { node: VisualNode; coneHalfAngle: number; innerRadius: number };
    const pending: PendingRing[] = [];
    packageNodes.forEach(node => {
        pending.push({ node, coneHalfAngle: packageCone,
            innerRadius: layout.boundsRadius });
    });
    // Horizontal mode retains nested cones. Standing mode is packed after collecting the tree.
    for (let cursor = 0; cursor < pending.length; cursor++) {
        const { node, coneHalfAngle, innerRadius } = pending[cursor]!;
        const children = treeByPath.get(node.path)!.childPaths.map(path => treeByPath.get(path)!);
        if (!children.length) continue;
        const sphereRadius = Math.min(Math.max(maxSphereRadius, maxFileRadius), children.length > 1
            ? folderRadius * Math.sin(Math.PI / children.length) * 0.7 : folderRadius * 0.35);
        const minimumNodeRadius = Math.min(sphereRadius, maxSphereRadius, maxFileRadius);
        const clearanceRadius = folderRadius + sphereRadius + gap;
        const start = new Vector3(...layout.entryByPath.get(node.path)!.position);
        const direction = start.clone().normalize();
        const outerDistance = standing ? start.length() + settings.radialLength
            : Math.max(start.length() + settings.radialLength,
                innerRadius + clearanceRadius + gap, clearanceRadius * 2 / Math.sin(coneHalfAngle));
        if (!Number.isFinite(outerDistance) || minimumNodeRadius * 2 / Math.sqrt(3) < 0.0001
            || outerDistance * Number.EPSILON > minimumNodeRadius * 1e-4) {
            throw new Error(`Ring-tree layout exceeds numeric precision at ${node.path}.`);
        }
        const centerVector = direction.clone().multiplyScalar(outerDistance);
        const center: Vector3Tuple = [centerVector.x, centerVector.y, centerVector.z];
        const quaternion = standing ? new Quaternion().setFromEuler(new Euler(...parallelRotation))
            : new Quaternion().setFromUnitVectors(new Vector3(0, 1, 0), direction);
        const euler = new Euler().setFromQuaternion(quaternion, 'XYZ');
        const rotation: Vector3Tuple = [euler.x, euler.y, euler.z];
        const ringNodes: VisualNode[] = children.map(child => ({ ...child, childPaths: [] }));
        node.childPaths = ringNodes.map(child => child.path);
        nodes.push(...ringNodes);
        layout.ringCenterByPath.set(node.path, center);
        layout.ringRadiusByPath.set(node.path, folderRadius);
        rotationByPath.set(node.path, rotation);
        const tilt = Math.atan2(folderRadius, outerDistance);
        const siblingHalfAngle = children.length > 1
            ? Math.asin(Math.sin(tilt) * Math.sin(Math.PI / children.length)) : coneHalfAngle;
        const childConeHalfAngle = Math.min(coneHalfAngle - tilt, siblingHalfAngle) * 0.9;
        ringNodes.forEach((child, childIndex) => {
            const phi = childIndex * Math.PI * 2 / ringNodes.length;
            const world = new Vector3(folderRadius * Math.cos(phi), 0, folderRadius * Math.sin(phi))
                .applyQuaternion(quaternion).add(centerVector);
            const position: Vector3Tuple = [world.x, world.y, world.z];
            const radius = Math.min(sphereRadius, child.nodeKind === 'directory' ? maxSphereRadius : maxFileRadius);
            radiusByPath.set(child.path, radius);
            layout.entryByPath.set(child.path, { position, angle: phi, parentPath: node.path, visualRadius: radius, subtreeRadius: radius });
        });
        const nextCone = standing ? packageCone : childConeHalfAngle;
        ringNodes.forEach(child => {
            if (child.nodeKind === 'directory') {
                pending.push({ node: child, coneHalfAngle: nextCone,
                    innerRadius: outerDistance + clearanceRadius });
            }
        });
        childRings.push({ parentPath: node.path, center, rotation, nodes: ringNodes, sphereRadius, coneHalfAngle, clearanceRadius });
        layout.boundsRadius = Math.max(layout.boundsRadius, outerDistance + clearanceRadius);
        if (node.depth === 1) initialBoundsRadius = Math.max(initialBoundsRadius, outerDistance + clearanceRadius);
    }
    if (standing && childRings.length) {
        compactStandingLayout(layout, childRings, settings.radialLength, gap);
        for (const ring of childRings) rotationByPath.set(ring.parentPath, ring.rotation);
        initialBoundsRadius = Math.max(...nodes.filter(node => node.depth <= 2).map(node => {
            const entry = layout.entryByPath.get(node.path)!;
            return new Vector3(...entry.position).length() + entry.visualRadius;
        }), ...childRings.filter(ring => ring.nodes[0]!.depth <= 2).map(ring =>
            new Vector3(...ring.center).length() + ring.clearanceRadius));
    }
    return { nodes, layout, rotationByPath, radiusByPath, childRings, initialBoundsRadius };
}
